<template>
  <div class="template-options-form">
    <div v-for="(section, sectionIndex) in sections" :key="section.key" class="template-options-section">
      <button
        v-if="section.header"
        type="button"
        class="section-header"
        :aria-expanded="!isCollapsed(sectionIndex)"
        @click="toggleSection(sectionIndex)">
        <div class="section-header-text">
          <h3 class="mb-1">{{ section.header.properties.title }}</h3>
          <small v-if="section.header.properties.subtitle" class="text-muted">
            {{ section.header.properties.subtitle }}
          </small>
        </div>
        <i
          class="ti section-header-chevron"
          :class="isCollapsed(sectionIndex) ? 'ti-chevron-down' : 'ti-chevron-up'"></i>
      </button>

      <div v-show="!isCollapsed(sectionIndex)" class="section-body">
        <template v-for="(def, index) in section.fields" :key="def.properties.key ?? `field-${index}`">
          <ImageOptionInput
            v-if="def.type === 'input.image'"
            :label="def.properties.label"
            :description="def.properties.description"
            :required="def.properties.required"
            :email-role="def.properties.email_role"
            :company-id="companyId"
            :modelValue="getOption(def.properties.key) ?? null"
            @update:modelValue="setOption(def, $event)" />

          <FormInput
            v-else-if="def.type === 'input.text' || def.type === 'input.imageUrl'"
            :label="def.properties.label"
            :placeholder="def.properties.placeholder"
            :hint="def.properties.description"
            :isRequired="def.properties.required"
            :modelValue="getOption(def.properties.key)"
            @update:modelValue="setOption(def, $event)"
            class="mb-3" />

          <!-- plain native textarea, not lithium's FormInput: its type="textarea" branch never
               actually binds the DOM value, so the field always renders empty -->
          <div v-else-if="def.type === 'input.textarea'" class="mb-3">
            <label v-if="def.properties.label" class="form-label" :class="{ required: def.properties.required }">
              {{ def.properties.label }}
            </label>
            <textarea
              class="form-control"
              style="resize: vertical"
              :rows="def.properties.rows || 4"
              :placeholder="def.properties.placeholder"
              :value="getOption(def.properties.key)"
              @input="setOption(def, ($event.target as HTMLTextAreaElement).value)"></textarea>
            <small v-if="def.properties.description" class="form-text text-muted">
              {{ def.properties.description }}
            </small>
          </div>

          <FormInput
            v-else-if="def.type === 'input.checkbox'"
            :label="def.properties.label"
            :hint="def.properties.description"
            type="switch"
            :modelValue="!!getOption(def.properties.key)"
            @update:modelValue="setOption(def, $event)"
            class="mb-3" />

          <div v-else-if="def.type === 'input.color'" class="mb-3">
            <label v-if="def.properties.label" class="form-label">
              {{ def.properties.label }}
              <EmailRoleBadge :role="def.properties.email_role" />
            </label>
            <div class="d-flex align-items-center gap-2">
              <PopsColorPicker
                :modelValue="(getOption(def.properties.key) as string) ?? ''"
                @update:modelValue="setOption(def, $event)" />
              <code class="text-muted">{{ getOption(def.properties.key) }}</code>
            </div>
            <small v-if="def.properties.description" class="form-text text-muted">
              {{ def.properties.description }}
            </small>
          </div>

          <div v-else-if="def.type === 'input.icon'" class="mb-3">
            <label v-if="def.properties.label" class="form-label">{{ def.properties.label }}</label>
            <PopsIconPicker
              :modelValue="(getOption(def.properties.key) as string) ?? ''"
              :icons-url="iconsUrl"
              @update:modelValue="setOption(def, $event)" />
            <small v-if="def.properties.description" class="form-text text-muted">
              {{ def.properties.description }}
            </small>
          </div>

          <div v-else-if="def.type === 'input.select'" class="mb-3">
            <label v-if="def.properties.label" class="form-label">{{ def.properties.label }}</label>
            <v-select
              :options="def.properties.options"
              :reduce="(opt: any) => opt.value"
              label="label"
              :modelValue="getOption(def.properties.key)"
              @update:modelValue="setOption(def, $event)"
              :placeholder="def.properties.placeholder || $t('publication.editor.select_placeholder')" />
            <small v-if="def.properties.description" class="form-text text-muted">
              {{ def.properties.description }}
            </small>
          </div>

          <div v-else-if="def.type === 'input.multiselect'" class="mb-3">
            <label v-if="def.properties.label" class="form-label">{{ def.properties.label }}</label>
            <v-select
              :options="def.properties.options"
              :reduce="(opt: any) => opt.value"
              label="label"
              :multiple="true"
              :modelValue="getOption(def.properties.key) ?? []"
              @update:modelValue="setOption(def, $event)"
              :placeholder="def.properties.placeholder || $t('publication.editor.select_placeholder')" />
            <small v-if="def.properties.description" class="form-text text-muted">
              {{ def.properties.description }}
            </small>
          </div>

          <div v-else-if="def.type === 'input.complex' && def.properties.schema" class="mb-3">
            <label v-if="def.properties.label" class="form-label">{{ def.properties.label }}</label>
            <small v-if="def.properties.description" class="form-text text-muted d-block mb-2">
              {{ def.properties.description }}
            </small>
            <CustomJsonFormsRenderer
              :schema="def.properties.schema"
              :data="getOption(def.properties.key)"
              @change="setOption(def, $event)" />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { FormInput } from '@prospective/lithium';
import { PopsColorPicker, PopsIconPicker } from '@prospective/pops-client-vue';
import CustomJsonFormsRenderer from '@/components/orders/creation/subcomponents/CustomJsonFormsRenderer.vue';
import EmailRoleBadge from '@/components/orders/creation/subcomponents/EmailRoleBadge.vue';
import ImageOptionInput from '@/components/orders/creation/subcomponents/ImageOptionInput.vue';
import type { TemplateOptionDef } from '@/types/templateMeta';
import { hostname } from '@/api/client';

interface TemplateOptionSection {
  key: string;
  header: TemplateOptionDef | null;
  fields: TemplateOptionDef[];
}

const props = defineProps<{
  optionDefs: TemplateOptionDef[];
  modelValue: Record<string, unknown>;
  companyId?: number;
}>();

const iconsUrl = `${hostname}/v1/icons`;

// group the flat option list into sections delimited by section.header entries.
// fields appearing before the first header form an initial header-less section.
const sections = computed<TemplateOptionSection[]>(() => {
  const result: TemplateOptionSection[] = [];
  let current: TemplateOptionSection | null = null;

  props.optionDefs.forEach((def, index) => {
    if (def.type === 'section.header') {
      current = { key: def.properties.key ?? `section-${index}`, header: def, fields: [] };
      result.push(current);
    } else if (def.properties.key) {
      if (!current) {
        current = { key: `section-${index}`, header: null, fields: [] };
        result.push(current);
      }
      current.fields.push(def);
    }
  });

  return result;
});

// sections start collapsed; an index present here has been expanded by the user.
const expanded = ref<Record<number, boolean>>({});

function isCollapsed(sectionIndex: number): boolean {
  return !expanded.value[sectionIndex];
}

function toggleSection(sectionIndex: number) {
  expanded.value = { ...expanded.value, [sectionIndex]: !expanded.value[sectionIndex] };
}

function expandSectionForKey(key: string): boolean {
  const sectionIndex = sections.value.findIndex((section) => section.fields.some((def) => def.properties.key === key));
  if (sectionIndex === -1) return false;
  expanded.value = { ...expanded.value, [sectionIndex]: true };
  return true;
}

const emit = defineEmits<{
  'update:modelValue': [value: Record<string, unknown>];
  'option-change': [payload: { key: string; value: unknown; forcesRefresh: boolean }];
}>();

function getOption(key: string | undefined): any {
  return key ? props.modelValue[key] : undefined;
}

function setOption(def: TemplateOptionDef, value: unknown) {
  const key = def.properties.key!;
  const updated = { ...props.modelValue, [key]: value };
  emit('update:modelValue', updated);
  emit('option-change', {
    key,
    value,
    forcesRefresh: !!def.properties.forces_refresh,
  });
}

defineExpose({ expandSectionForKey });
</script>

<style scoped>
/* break out of the container's horizontal padding so dividers span full width,
   then re-apply that padding inside each section so content stays aligned. */
.template-options-form {
  --section-padding-x: 1.5rem;
  margin-inline: calc(-1 * var(--section-padding-x));
}

/* each section is delimited by a full-width border and darkens as a whole on
   hover (divider to divider) to signal it is clickable. */
.template-options-section {
  padding-block: 0.75rem;
  border-bottom: 1px solid var(--tblr-border-color, #e6e7e9);
  transition: background-color 0.15s ease;
}

.template-options-section:last-child {
  border-bottom: none;
}

.template-options-section:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.section-header,
.section-body {
  padding-inline: var(--section-padding-x);
}

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  padding-top: 0;
  padding-bottom: 0;
  margin-bottom: 0.75rem;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
}

.section-header-text {
  flex: 1;
}

.section-header-chevron {
  flex-shrink: 0;
  margin-top: 0.125rem;
  color: var(--tblr-muted, #6c757d);
}
</style>
